import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'

import { ProtectedRoute } from '@webapp/app/components'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useFeatureFlag } from '@webapp/hooks'
import { useAppRoutes } from '@webapp/hooks/useAppRoutes'
import { IntegrationServicePlatforms } from '@webapp/models/enums'
import { APIApplicationPage } from '@webapp/pages/APIApplicationPage'
import { Dynamics365BusinessCentralPage } from '@webapp/pages/Dynamics365BusinessCentralPage'
import { IntegrationMergePage } from '@webapp/pages/IntegrationMergePage'
import { IntegrationNetsuitePage } from '@webapp/pages/IntegrationNetsuitePage'
import { IntegrationPlatformListPage } from '@webapp/pages/IntegrationPlatformListPage'
import { NetsuitePage } from '@webapp/pages/NetsuitePage'
import { QboPage } from '@webapp/pages/QboPage'
import { SageIntacctPage } from '@webapp/pages/SageIntacctPage'
import { useGetIntegrationServiceState } from '@webapp/platform/_unmigrated/hooks/useGetIntegrationServiceState'
import { Permissions } from '@webapp/platform/iam'
import { IntegrationNames } from '@webapp/platform/integrations/entities/enums/IntegrationNameEnum'
import { IntegrationDetailsPage } from '@webapp/platform/integrations/pages/IntegrationDetailsPage/IntegrationDetailsPage'
import { SlackIntegrationPage } from '@webapp/platform/integrations/pages/SlackIntegrationPage'
import { getCurrentNetSuiteEnvironment } from '@webapp/utils/integration/getCurrentNetSuiteEnvironment'

const IntegrationsRoute = ({ path }) => {
  return (
    <ProtectedRoute
      exact
      path={path}
      component={IntegrationPlatformListPage}
      permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
    />
  )
}

export const IntegrationsRouter = () => {
  const { brp } = useAppRoutes()

  const { api_application_setup: showApplicationSetup } = useSelector(
    makeSelectFeatureSwitches()
  )

  const PLAT_2155_MERGE_NETSUITE = useFeatureFlag('PLAT_2155_MERGE_NETSUITE')

  const { currentQboEnvironment, data } = useGetIntegrationServiceState()
  const hasMergeQboIntegration =
    currentQboEnvironment ===
    IntegrationServicePlatforms.MERGE_QUICKBOOKS_ONLINE

  const currentNetSuiteEnvironment = PLAT_2155_MERGE_NETSUITE
    ? getCurrentNetSuiteEnvironment(data)
    : IntegrationServicePlatforms.NETSUITE

  const hasMergeNetSuiteIntegration = PLAT_2155_MERGE_NETSUITE
    ? currentNetSuiteEnvironment === IntegrationServicePlatforms.MERGE_NETSUITE
    : false

  return (
    <Switch>
      <Route
        exact
        path={brp('NetsuitePage')}
        component={IntegrationNetsuitePage}
      />

      <Route
        path={brp('NetsuiteBillsPage')}
        render={(props) => {
          return hasMergeNetSuiteIntegration ? (
            <IntegrationDetailsPage
              platform={IntegrationNames.MERGE_NETSUITE}
            />
          ) : (
            <NetsuitePage {...props} />
          )
        }}
      />

      <Route
        path={brp('QboPage')}
        component={hasMergeQboIntegration ? IntegrationMergePage : QboPage}
      />

      <Route path={brp('SageIntacctPage')} component={SageIntacctPage} />

      <Route
        path={brp('Dynamics365BCPage')}
        component={Dynamics365BusinessCentralPage}
      />

      {showApplicationSetup && (
        <ProtectedRoute
          path={brp('ProcurifyApiPage')}
          component={APIApplicationPage}
          navigationPermissions={['settings']}
        />
      )}

      <ProtectedRoute
        exact
        path={brp('SlackIntegrationPage')}
        component={SlackIntegrationPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
      />

      <IntegrationsRoute path={brp('IntegrationsPage')} />
    </Switch>
  )
}
